import { DeepReadonly, Ref, ShallowRef, readonly, ref, shallowRef } from 'vue';
import axios, { AxiosError } from 'axios';
import { debounce } from 'lodash';
import {
  NotificationListResponse,
  RawNotificationListResponse,
  UserNotification,
} from '@/modules/common/models';
import { SocketNotification } from '@/utils/helpers/socket';
import { useSocketStore } from '@/store/socket-store';
import { useAuthStore } from '@/store/auth-store';
import { ApiError } from '@/utils/errors/api-error';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { socketDebouncConfig, socketDebounceTime } from '@/store/store.const';

// Module-level state (singleton pattern - shared across all component instances)
const notifications = shallowRef<UserNotification[]>([]);
const unreadNotificationCount = ref(0);
const totalNotificationCount = ref(0);
const lastNotification = ref<SocketNotification | null>(null);

let isSocketHandlerRegistered = false;

export interface UseNotificationsReturn {
  notifications: DeepReadonly<ShallowRef<UserNotification[]>>;
  unreadNotificationCount: DeepReadonly<Ref<number>>;
  totalNotificationCount: DeepReadonly<Ref<number>>;
  lastNotification: DeepReadonly<Ref<SocketNotification | null>>;
  fetchMyNotifications: () => Promise<void>;
  fetchMyNotificationCounts: () => Promise<void>;
  markNotificationRead: (ids: string[]) => Promise<void>;
  deleteNotifications: (ids: string[]) => Promise<void>;
}

export function useNotifications(): UseNotificationsReturn {
  const authStore = useAuthStore();

  async function fetchMyNotifications(): Promise<void> {
    if (authStore.user === null) return;

    try {
      const { data } = await axios.get<RawNotificationListResponse>('/api/1/me/notifications');
      const response = NotificationListResponse.fromData(data);
      notifications.value = response.notifications;
      totalNotificationCount.value = response.totalCount;
      unreadNotificationCount.value = response.unreadCount;
    } catch (e) {
      throw new ApiError(i18nServerMessage(e as AxiosError));
    }
  }

  async function fetchMyNotificationCounts(): Promise<void> {
    if (authStore.user === null) return;

    try {
      const { data } = await axios.get('/api/1/me/notifications/counts');
      totalNotificationCount.value = data.totalCount;
      unreadNotificationCount.value = data.unreadCount;
    } catch (e) {
      throw new ApiError(i18nServerMessage(e as AxiosError));
    }
  }

  async function markNotificationRead(ids: string[]): Promise<void> {
    try {
      await axios.post(
        `/api/1/me/notifications/mark-read`,
        ids.map((id) => ({ id, isRead: true }))
      );
      await fetchMyNotifications();
    } catch (e) {
      throw new ApiError(i18nServerMessage(e as AxiosError));
    }
  }

  async function deleteNotifications(ids: string[]): Promise<void> {
    try {
      await axios.post(`/api/1/me/notifications/delete`, ids);
      await fetchMyNotifications();
    } catch (e) {
      throw new ApiError(i18nServerMessage(e as AxiosError));
    }
  }

  // Register socket handler only once (singleton pattern)
  if (!isSocketHandlerRegistered) {
    isSocketHandlerRegistered = true;

    const refreshNotifications = debounce(
      (payload: SocketNotification) => {
        void fetchMyNotificationCounts().catch(() => {
          // ignore errors from socket-triggered refreshes
        });
        if (payload.notificationRefID != null) {
          // There is a new notification (not just an update of an existing one)
          lastNotification.value = payload;
        }
      },
      socketDebounceTime,
      socketDebouncConfig
    );

    const socketStore = useSocketStore();
    socketStore.useEventHandler('refreshNotifications', refreshNotifications);
  }

  return {
    notifications: readonly(notifications),
    unreadNotificationCount: readonly(unreadNotificationCount),
    totalNotificationCount: readonly(totalNotificationCount),
    lastNotification: readonly(lastNotification),
    fetchMyNotifications,
    fetchMyNotificationCounts,
    markNotificationRead,
    deleteNotifications,
  };
}
